import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  DestroyRef, 
  HostListener,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  TemplateRef,
  Type,
  ViewChild
} from "@angular/core";
import { ModalController } from "@ionic/angular/standalone";
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from "@ngx-translate/core";
import { CommonModule } from "@angular/common";
import { DynamicHostDirective } from "./dynamic-host"; 
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'; 
import { ModalStateService, DismissPayload } from "src/app/services/modal-service/modal-state.service";

export interface BaseModalConfig {
  innerComponent?: Type<any>;
  innerComponentInputs?: Record<string, unknown>;
  message?: string;
  header?: string;
  showHeader?: boolean;
  showCloseButton?: boolean;
  headerWithShadow?: boolean;
  centerHeaderTitle?: boolean;
  centerInnerContent?: boolean;
  showFooter?: boolean;
  showFooterButton?: boolean;
  showFooterShadow?: boolean;
  footerButtonText?: string;
  footerButtonColor?: string;
  footerButtonDisabled?: boolean;
  footerButtonExpandMode?: "block" | "full" | null;
  footerColumeSize?: string;
  footerAction?: (() => void | Promise<void>) | null;
  autoCloseEnabled?: boolean;
  autoCloseTimeout?: number; 
  headerTemplate?: TemplateRef<any> | null;
  footerTemplate?: TemplateRef<any> | null;
  [key: string]: unknown;
}

/**
 * Derives the plain-object shape of a component's signal inputs:
 *   { inputName: valueType }
 *
 * Example:
 *   class LeaveMessageModalComponent {
 *     readonly buttonName = input.required<string>();
 *     readonly isPortrait = input<boolean>();
 *   }
 *   // ModalComponentInputs<LeaveMessageModalComponent> =
 *   //   { buttonName: string; isPortrait: boolean | undefined }
 *
 * Used with `satisfies` in `modal-types.ts` so that a wrong input name or a
 * mismatched value type in `innerComponentInputs` fails at COMPILE time
 * instead of silently breaking the modal at runtime.
 *
 * Note: Angular 20+ ships this built-in as `ComponentInputs<T>` —
 * replace this helper with the built-in after upgrading.
 */
export type ModalComponentInputs<C> = {
  [K in InputSignalKeys<C>]: InputSignalValue<C, K>;
};

type InputSignalKeys<C> = {
  [K in keyof C]-?: C[K] extends InputSignal<unknown> ? K : never;
}[keyof C];

type InputSignalValue<C, K extends keyof C> =
  C[K] extends InputSignal<infer V> ? V : never;

@Component({
  selector: "app-base-modal",
  standalone: true,
  templateUrl: "./base-modal.component.html",
  styleUrls: ["./base-modal.component.scss"],
  imports: [CommonModule, DynamicHostDirective, TranslateModule, IonicModule],
  providers: [ModalStateService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseModalComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly config: InputSignal<BaseModalConfig> = input.required<BaseModalConfig>();
  @ViewChild(DynamicHostDirective) dynamicHost!: DynamicHostDirective;
  private modalStateService = inject(ModalStateService);

  header = computed(() => this.modalStateService.state().header ?? this.config().header ?? "");
  message = computed(() => this.modalStateService.state().message ?? this.config().message ?? "");
  showHeader = computed(() => this.modalStateService.state().showHeader ?? this.config().showHeader ?? true);
  showCloseButton = computed(() => this.modalStateService.state().showCloseButton ?? this.config().showCloseButton ?? true);
  headerWithShadow = computed(() => this.modalStateService.state().headerWithShadow ?? this.config().headerWithShadow ?? false);
  centerHeaderTitle = computed(() => this.modalStateService.state().centerHeaderTitle ?? this.config().centerHeaderTitle ?? false);
  centerInnerContent = computed(() => this.modalStateService.state().centerInnerContent ?? this.config().centerInnerContent ?? true);
  showFooter = computed(() => this.modalStateService.state().showFooter ?? this.config().showFooter ?? false);
  showFooterButton = computed(() => this.modalStateService.state().showFooterButton ?? this.config().showFooterButton ?? false);
  showFooterShadow = computed(() => this.config().showFooterShadow ?? false);
  footerButtonText = computed(() => this.modalStateService.state().footerButtonText ?? this.config().footerButtonText ?? "OK");
  footerButtonColor = computed(() => this.modalStateService.state().footerButtonColor ?? this.config().footerButtonColor ?? "dark");
  footerButtonDisabled = computed(() => this.modalStateService.state().footerButtonDisabled ?? this.config().footerButtonDisabled ?? false);
  footerColumeSize = computed(() => this.config().footerColumeSize ?? "auto");
  footerButtonExpandMode = computed(() => this.modalStateService.state().footerButtonExpandMode ?? this.config().footerButtonExpandMode ?? null);
  footerButtonAction = computed(() => this.modalStateService.state().footerAction ?? this.config().footerAction ?? null);
  dynamicHeaderTemplate = computed(() => this.modalStateService.state().headerTemplate ?? this.config().headerTemplate ?? null);
  dynamicFooterTemplate = computed(() => this.modalStateService.state().footerTemplate ?? this.config().footerTemplate ?? null);
  autoCloseEnabled = computed(() => this.config().autoCloseEnabled ?? true);
  autoCloseTimeout = computed(() => this.config().autoCloseTimeout ?? 120);
  accessPinCloseButton = computed(() => this.config().accessPinCloseButton === true);

  
  protected modalCtrl = inject(ModalController);
  private destroyRef = inject(DestroyRef); 

  
  private innerComponentRef: ComponentRef<unknown> | null = null;
  private autoCloseTimer?: NodeJS.Timeout;

  ngOnInit(): void {

    this.modalStateService.setConfig(this.config());
    this.initializeAutoCloseLogic();
    
    this.modalStateService.close$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.executeFooterAction());

      this.modalStateService.dismiss$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload: DismissPayload) => {
          this.dismissModal(payload.role, payload.data);
      });

    
  }

  ngAfterViewInit(): void {
    this.loadInnerComponent();
  }

  ngOnDestroy(): void {
    
    this.clearAutoCloseTimer();
    
    if (this.innerComponentRef) {
      this.innerComponentRef.destroy();
      this.innerComponentRef = null;
    }
    if (this.dynamicHost) {
      this.dynamicHost.viewContainerRef.clear();
    }
  }

  @HostListener('window:mousemove')
  @HostListener('window:keydown')
  @HostListener('window:click')
  protected resetAutoCloseTimer(): void {
    
    if (this.autoCloseEnabled() && this.autoCloseTimeout() > 0) {
        this.clearAutoCloseTimer();
        this.startAutoCloseTimer(this.autoCloseTimeout());
    }
  }

  private startAutoCloseTimer(timeoutSeconds: number): void {
      this.clearAutoCloseTimer(); 
      this.autoCloseTimer = setTimeout(() => {
          this.dismissModal('auto-close');
      }, timeoutSeconds * 1000);
  }

  private clearAutoCloseTimer(): void {
    if (this.autoCloseTimer) {
        clearTimeout(this.autoCloseTimer);
        this.autoCloseTimer = undefined;
    }
  }

  private initializeAutoCloseLogic(): void {
    this.clearAutoCloseTimer();
    if (this.autoCloseEnabled() && this.autoCloseTimeout() > 0) {
      this.startAutoCloseTimer(this.autoCloseTimeout());
    }
  }

  private loadInnerComponent(): void {
    const currentConfig = this.config();
    const componentType = currentConfig?.innerComponent;

    if (componentType && this.dynamicHost) {
      const viewContainerRef = this.dynamicHost.viewContainerRef;
      viewContainerRef.clear();
 
        const componentRef = viewContainerRef.createComponent(componentType);
        this.innerComponentRef = componentRef;
        this.passInputsToInnerComponent(componentRef);
        componentRef.changeDetectorRef.detectChanges();
    } else {
        if (this.dynamicHost) {
             this.dynamicHost.viewContainerRef.clear();
        }
    }
  }

  private passInputsToInnerComponent(componentRef: ComponentRef<unknown>) {
    const inputs: Record<string, unknown> =
      this.config().innerComponentInputs ?? {};

    Object.keys(inputs).forEach((key) => {
      componentRef.setInput(key, inputs[key]);
    });
  }

  async executeFooterAction(): Promise<void> {
    const action = this.footerButtonAction();
    this.clearAutoCloseTimer();
    if (typeof action === "function") {
        await Promise.resolve(action());
    } else {
      this.dismissModal('confirm'); 
    }
  }

  dismissModal(role: string = 'cancel', data?: any): void {
    this.clearAutoCloseTimer(); 
    this.modalCtrl.dismiss(data, role);
  }
}
